import type { AxiosResponse } from 'axios'

const DEFAULT_TTL_MS = 30000
const DEFAULT_MAX_ENTRIES = 50

interface CachedResponse {
  response: AxiosResponse
  expiresAt: number
}

function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value)
  }

  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringify(item)).join(',')}]`
  }

  const record = value as Record<string, unknown>
  const keys = Object.keys(record).sort()
  const serialized = keys.map((key) => `${JSON.stringify(key)}:${stableStringify(record[key])}`)
  return `{${serialized.join(',')}}`
}

export function shouldCacheCalculatorRequest(endpoint: string): boolean {
  return /^\/api\/v1\/calculators\/.+\/calculate$/.test(endpoint)
}

export function buildCalculatorCacheKey(endpoint: string, payload?: Record<string, unknown>): string {
  return `${endpoint}::${stableStringify(payload ?? {})}`
}

export class CalculatorResponseCache {
  private entries = new Map<string, CachedResponse>()

  constructor(
    private readonly ttlMs = DEFAULT_TTL_MS,
    private readonly maxEntries = DEFAULT_MAX_ENTRIES
  ) {}

  get(key: string): AxiosResponse | null {
    const cached = this.entries.get(key)
    if (!cached) {
      return null
    }

    if (Date.now() > cached.expiresAt) {
      this.entries.delete(key)
      return null
    }

    return cached.response
  }

  set(key: string, response: AxiosResponse): void {
    if (this.entries.size >= this.maxEntries) {
      const oldest = this.entries.keys().next().value
      if (oldest) {
        this.entries.delete(oldest)
      }
    }

    this.entries.set(key, {
      response,
      expiresAt: Date.now() + this.ttlMs,
    })
  }
}
